import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { openUnassignedSpendingPrompt } from '@/components/spending/UnassignedSpendingPromptDialog';

interface SharedBudgetBadgeProps {
  shared?: boolean;
  owned?: boolean;
  className?: string;
}

export function SharedBudgetBadge({ shared, owned, className }: SharedBudgetBadgeProps) {
  if (owned === false) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs text-primary font-medium', className)}>
        Shared with you
      </span>
    );
  }

  if (shared) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs text-muted-foreground', className)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        Shared
      </span>
    );
  }

  return null;
}

interface InvitationsNavIconProps {
  pendingCount: number;
  className?: string;
  iconClassName?: string;
}

export function InvitationsNavIcon({ pendingCount, className, iconClassName }: InvitationsNavIconProps) {
  const hasPending = pendingCount > 0;

  return (
    <Link
      to="/invitations"
      className={cn('relative inline-flex items-center justify-center', className)}
      title={hasPending ? `${pendingCount} pending invitation(s)` : 'Invitations'}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(hasPending ? 'text-primary' : 'text-muted-foreground', iconClassName)}
      >
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
      {hasPending && (
        <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-2 ring-background">
          {pendingCount > 9 ? '9+' : pendingCount}
        </span>
      )}
    </Link>
  );
}

interface UnassignedSpendingNavIconProps {
  pendingCount: number;
  className?: string;
  iconClassName?: string;
}

export function UnassignedSpendingNavIcon({
  pendingCount,
  className,
  iconClassName,
}: UnassignedSpendingNavIconProps) {
  if (pendingCount <= 0) return null;

  return (
    <button
      type="button"
      onClick={() => openUnassignedSpendingPrompt()}
      className={cn('relative inline-flex items-center justify-center', className)}
      title={`${pendingCount} unassigned purchase(s)`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn('text-warning', iconClassName)}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
      <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-[10px] font-bold text-white ring-2 ring-background">
        {pendingCount > 9 ? '9+' : pendingCount}
      </span>
    </button>
  );
}
